import { inject, Injectable } from '@angular/core';
import { Params } from '@angular/router';
import { SharedAppSettingsService } from '@luggagehero/shared/app-settings/data-access';
import { SharedStorageService } from '@luggagehero/shared/services/storage';
import { Observable, ReplaySubject } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class SharedSplitTestingService {
  private settings = inject(SharedAppSettingsService);
  private storage = inject(SharedStorageService);

  private initPromise: Promise<void>;
  private initResolve!: () => void;

  // ReplaySubject (not seeded) so subscribers and `combineLatest` never observe a value
  // before `init()` runs — `FeatureResolver` relies on this to avoid resolving a feature
  // with a premature/null variant during app bootstrap.
  private readonly variantSubject = new ReplaySubject<string | null>(1);

  /** Emits the active variant once `init()` has run (and again on any later re-init). */
  public readonly variant$: Observable<string | null> = this.variantSubject.asObservable();

  /** Gets the current variant to apply for the clieent; or null if no variant is enabled. */
  public get variant(): string | null {
    if (this.storage.variant !== null && this.storage.variant !== undefined) {
      return this.storage.variant;
    }
    return this.settings.current?.variantId || null;
  }

  /** Gets the current experiment which the client is part of; or null if no experiment is enabled. */
  public get experiment(): string | null {
    if (this.storage.experimentId !== null && this.storage.experimentId !== undefined) {
      return this.storage.experimentId;
    }
    return this.settings.current?.experimentId || null;
  }

  constructor() {
    // Initialize the initPromise and store its resolve function
    this.initPromise = new Promise<void>((resolve) => (this.initResolve = resolve));
  }

  /**
   * Initializes the split testing service by configuring experiment and variant to apply (if any). If no experiment or
   * variant is provided with the given params, the service will use values from app settings, if available.
   * @param params The query parameters from the current route
   * @returns void
   */
  public init(params: Params): void {
    const variant: string | undefined =
      (params['utm_variant'] as string) ??
      (params['lh_variant'] as string) ??
      (params['variant'] as string) ??
      undefined;

    if (variant !== undefined && variant !== null) {
      // Use the variant from the query params if provided
      this.storage.experimentId = '';
      this.storage.variant = String(variant);
    } else {
      // Otherwise, use experiment and variant ids from app settings (retrieved from the server)
      this.storage.experimentId = this.settings.current?.experimentId ?? '';
      this.storage.variant = this.settings.current?.variantId ?? '';
    }

    // Notify reactive consumers (e.g. FeatureResolver) that the variant is now resolved.
    this.variantSubject.next(this.variant);

    // Resolve the initPromise to indicate that initialization is complete
    if (this.initResolve) {
      this.initResolve();
      this.initResolve = undefined as unknown as () => void;
    }
  }

  /**
   * Determines if a given variant is currently enabled. Waits for the service to be initialized
   * before returning. This is pure variant plumbing — any feature-specific interpretation
   * (settings overrides, runtime context gates, variant→value mapping) lives in `FeatureResolver`.
   * @param variant The variant to check.
   * @returns A Promise that resolves to true if the specified variant is enabled; otherwise, false.
   */
  public async isVariantEnabled(variant: string): Promise<boolean> {
    // Wait for init() to be called and completed
    await this.initPromise;

    return this.variant === variant;
  }
}
